import { LayoutLoading } from "@/components/layout-loading/layout-loading";
import { useToastError } from "@/components/ui/use-toast";
import { useApi } from "@/hooks/useApi";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import {
  markSupportMessengerBooted,
  shutdownSupportMessenger,
} from "@/lib/support";
import { initialSupportLauncherSettings } from "@/lib/support-launcher-placement";
import { useUser as useClerkUser } from "@clerk/react";
import Intercom from "@intercom/messenger-js-sdk";
import { Nullable, TeamUserDTO, UserMeDTO } from "@shaarp/common";
import { usePostHog } from "posthog-js/react";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTeams } from "./teams-provider";

type UserContextType = {
  user: Nullable<UserMeDTO>;
  teamUser: Nullable<TeamUserDTO>;
  refreshTeamUser: () => Promise<void>;
};

const UserContext = createContext<UserContextType>({
  user: null,
  teamUser: null,
  refreshTeamUser: async () => {},
});

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within an UserProvider");
  }
  return context;
};

export const UserProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const toastError = useToastError();
  const api = useApi();
  const { user } = useClerkUser();
  const { teamSelected } = useTeams();
  const { isMobile } = useDeviceFormat();
  const posthog = usePostHog();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [currentUser, setCurrentUser] = useState<Nullable<UserMeDTO>>(null);

  const [currentTeamUser, setCurrentTeamUser] =
    useState<Nullable<TeamUserDTO>>(null);

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  useEffect(() => {
    if (teamSelected) {
      fetchCurrentTeamUser();
    }
  }, [teamSelected]);

  useEffect(() => {
    if (user && user.id) {
      posthog?.identify(user.id, {
        email: user.primaryEmailAddress?.emailAddress,
        name: user.fullName as string,
        first_name: user.firstName,
        last_name: user.lastName,
        avatar_url: user.imageUrl,
        created_at: user.createdAt?.toISOString(),
        ...(currentUser && {
          admin_level: currentUser.adminLevel,
          is_advisor: currentUser.isAdvisor ?? false,
        }),
      });
    }
  }, [posthog, user, currentUser]);

  useEffect(() => {
    if (
      user &&
      user.id &&
      currentUser &&
      import.meta.env.VITE_INTERCOM_APP_ID &&
      currentUser.intercomUserHash
    ) {
      Intercom({
        app_id: import.meta.env.VITE_INTERCOM_APP_ID,
        user_id: user.id,
        user_hash: currentUser.intercomUserHash,
        name: user.fullName as string,
        email: user.primaryEmailAddress?.emailAddress,
        // Intercom reads Unix seconds; milliseconds put the sign-up date in the
        // far future and fight the API sync's `signed_up_at` on every boot.
        created_at: user.createdAt
          ? Math.floor(user.createdAt.getTime() / 1000)
          : undefined,
        // On desktop the launcher is a permanent support entry point; from
        // here on `SupportLauncher` keeps it clear of whatever shares its
        // corner. On a phone it stays hidden — support lives in the account
        // menu there.
        ...initialSupportLauncherSettings(!isMobile),
        company: teamSelected
          ? {
              company_id: teamSelected.id,
              name: teamSelected.name,
            }
          : undefined,
      });
      markSupportMessengerBooted();
    }
  }, [Intercom, user, currentUser, teamSelected, isMobile]);

  // The messenger is booted here, so it is torn down here too: signing out
  // unmounts this provider (`<Show when="signed-in">` in `main.tsx`), and a
  // messenger left running would keep answering as the user who just left,
  // with every in-app entry point still offering their conversation.
  useEffect(() => () => shutdownSupportMessenger(), []);

  const fetchCurrentUser = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get<UserMeDTO>(`/my/user`);
      setCurrentUser(data);
    } catch (error) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCurrentTeamUser = useCallback(async () => {
    const { data } = await api.get<TeamUserDTO>(`/my/team/me`);
    setCurrentTeamUser(data);
  }, [api]);

  const value = useMemo(
    () => ({
      user: currentUser,
      teamUser: currentTeamUser,
      refreshTeamUser: fetchCurrentTeamUser,
    }),
    [currentUser, currentTeamUser, fetchCurrentTeamUser],
  );

  if (isLoading) {
    return <LayoutLoading />;
  }

  return (
    <UserContext.Provider value={value}>{children}</UserContext.Provider>
  );
};
